import { useState, useRef, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Badge } from '@/components/ui/badge';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Communication, CommunicationDelivery } from '@shared/schema';
import { UnifiedButton } from '@/components/ui/UnifiedButton';

interface UserCommunication extends CommunicationDelivery {
  communication: Communication;
  sender?: {
    id: string;
    username: string | null;
    displayName: string | null;
    role: string;
    profileImage: string | null;
  };
}

interface NotificationBellProps {
  isAuthenticated: boolean;
}

/**
 * NotificationBell component displays a bell icon with unread notification count
 * and a dropdown showing recent notifications.
 */
export function NotificationBell({ isAuthenticated }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user communications if authenticated
  const { data: userCommunications = [] } = useQuery<UserCommunication[]>({
    queryKey: ['/api/user-communications'],
    enabled: isAuthenticated,
    refetchInterval: 30000, // Check for new messages every 30 seconds
  });

  // Mutation to mark communication as read
  const markAsReadMutation = useMutation({
    mutationFn: async (deliveryId: string) => {
      return apiRequest('POST', `/api/communications/${deliveryId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user-communications'] });
    },
  });

  // Mutation to mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('PUT', '/api/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user-communications'] });
    },
  });

  // Get unread communications (null-safe: query client returns null on 401)
  const unreadCommunications = (userCommunications ?? []).filter(comm => comm.status !== 'read');

  // Helper function to get icon for communication type
  const _getCommunicationIcon = (_type: string, _priority: string) => {
    // Simplified - just return Bell for now
    return Bell;
  };

  // Helper function to get priority color
  const _getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'normal':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-cyber-cyan/20 text-cyber-cyan border-cyber-cyan/30';
    }
  };

  // Get role icon
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'instructor':
        return '👨‍🏫';
      case 'admin':
        return '⚙️';
      case 'counselor':
        return '💬';
      case 'consultant':
        return '🎯';
      default:
        return '🤖';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <UnifiedButton
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-[1.3rem] hover:bg-glass-medium transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6 text-white" />
        {unreadCommunications.length > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-cyber-cyan text-dark-950 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
            data-testid="notification-count"
          >
            {unreadCommunications.length > 9 ? '9+' : unreadCommunications.length}
          </span>
        )}
      </UnifiedButton>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[600px] overflow-y-auto bg-dark-900 border border-glass-medium rounded-[1.3rem] shadow-2xl z-50"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="sticky top-0 bg-dark-900 border-b border-glass-medium p-4 rounded-t-[1.3rem]">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold text-lg">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCommunications.length > 0 && (
                  <>
                    <UnifiedButton
                      variant="ghost"
                      size="sm"
                      onClick={() => markAllAsReadMutation.mutate()}
                      disabled={markAllAsReadMutation.isPending}
                      className="text-xs text-cyber-cyan hover:text-white px-2 py-1"
                    >
                      {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all read'}
                    </UnifiedButton>
                    <Badge className="bg-cyber-cyan/20 text-cyber-cyan border-cyber-cyan/30 text-xs">
                      {unreadCommunications.length} new
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Notification List */}
          <div className="p-2">
            {unreadCommunications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-dark-400 mx-auto mb-3" />
                <p className="text-dark-300 text-sm">No new notifications</p>
              </div>
            ) : (
              <div className="space-y-2">
                {unreadCommunications.slice(0, 5).map(comm => {
                  const sender = comm.sender || {
                    displayName: 'W.Rabbit',
                    username: 'system',
                    role: 'admin',
                    profileImage: null,
                    id: 'system',
                  };
                  const senderName = sender.displayName || sender.username || 'Unknown';

                  return (
                    <div
                      key={comm.id}
                      className="p-3 bg-glass-light hover:bg-glass-medium rounded-[1.3rem] transition-colors"
                      data-testid={`notification-item-${comm.id}`}
                    >
                      <div className="flex items-start justify-between space-x-3">
                        <div className="flex-1 min-w-0">
                          {/* Sender Attribution */}
                          <div className="flex items-center space-x-2 mb-1">
                            {sender.profileImage && (
                              <img
                                src={sender.profileImage ?? undefined}
                                alt={senderName}
                                className="w-4 h-4 rounded-full object-cover"
                              />
                            )}
                            <span className="text-cyber-cyan text-xs font-medium">
                              {getRoleIcon(sender.role)} {senderName}
                            </span>
                            <span className="text-dark-400 text-xs">•</span>
                            <span className="text-dark-400 text-xs">
                              {new Date(comm.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </div>

                          {/* Title */}
                          <h4 className="text-white font-medium text-sm mb-1 line-clamp-1">
                            {comm.communication.title}
                          </h4>

                          {/* Content Preview */}
                          <p className="text-dark-300 text-xs line-clamp-2">
                            {comm.communication.content}
                          </p>

                          {/* Action link from targetCriteria.actionUrl */}
                          {(() => {
                            const criteria = comm.communication.targetCriteria as Record<
                              string,
                              unknown
                            > | null;
                            const actionUrl = criteria?.actionUrl as string | undefined;
                            if (!actionUrl) return null;
                            return (
                              <Link
                                href={actionUrl}
                                className="inline-block mt-1 text-xs text-cyber-cyan hover:text-cyber-cyan/80 underline underline-offset-2"
                                onClick={() => setIsOpen(false)}
                              >
                                View on calendar →
                              </Link>
                            );
                          })()}
                        </div>

                        {/* Dismiss Button */}
                        <UnifiedButton
                          onClick={e => {
                            e.stopPropagation();
                            markAsReadMutation.mutate(comm.id);
                          }}
                          className="text-dark-400 hover:text-white transition-colors flex-shrink-0"
                          data-testid={`dismiss-${comm.id}`}
                        >
                          <X className="w-4 h-4" />
                        </UnifiedButton>
                      </div>
                    </div>
                  );
                })}

                {unreadCommunications.length > 5 && (
                  <div className="text-center pt-2">
                    <p className="text-dark-400 text-xs">
                      And {unreadCommunications.length - 5} more...
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - View All Link */}
          <div className="sticky bottom-0 bg-dark-900 border-t border-glass-medium p-3 rounded-b-[1.3rem]">
            <Link href="/notifications">
              <a
                className="block text-center text-cyber-cyan hover:text-white text-sm font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                View All Notifications
              </a>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
